import axios, {AxiosError} from 'axios'
import {getSelectedOrgId} from '@/tenants/org.service'
import {resolvePracsysApiPath} from '@/services/infrastructure/ServerBasePathResolver'
import {Problem} from '@/types/Problem'
import {Either, Left, Right} from '@/types/either/Either'
import {getAccessToken} from '@/services/infrastructure/auth.service'
import {session} from '@/services/infrastructure/session.service'

export const getHeaders = async (tenantId?: string): Promise<any> => {
    const accessToken = await getAccessToken()
    if (!accessToken) {
        console.error('Attempting to call backend API without a valid access token! Should not happen!')
        return
    }

    return {
        Authorization: `Bearer ${accessToken}`,
        tenantId: tenantId || getSelectedOrgId(),
        sourceRef: session.value
    }
}

const doCatch = async <VALUE_TYPE>(block: () => Promise<VALUE_TYPE>): Promise<Either<Problem, VALUE_TYPE>> => {
    try {
        return Right.create(await block())
    } catch (e) {
        if (e instanceof AxiosError && e.response?.data) {
            console.error(e)
            return Left.create(e.response.data)
        }
        throw e
    }
}

//TODO: instead of using tenantId as optional parameter, use a full headers-object and allow overwriting with destructuring (like in POST)
// TODO fucks up any non tenant specific requests
const get = async <T> (path: string, params: any = {}, tenantId?: string): Promise<T | undefined> => {

    const requestKey = `${getSelectedOrgId()}_${tenantId}`
    const response = await axios.get(
        resolvePracsysApiPath() + path,
        {
            headers: await getHeaders(tenantId),
            params: {
                ...Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined))
            }
        }
    )
    const responseKey = `${getSelectedOrgId()}_${tenantId}`
    if (requestKey !== responseKey) {
        console.warn('Tenant ID in request and response do not match. Possibly timing issue', {path, params, requestKey, responseKey})
        return undefined
    }

    return response.data
}

const anonymousGet = async (path: string, params: any = {}) => {
    const response = await axios.get(
        resolvePracsysApiPath() + path,
        {
            params: {
                ...Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined))
            }
        }
    )
    return response.data
}

const post = async (path: string, body: any, headers: any = {}, params: any = {}) => {
    const response = await axios.post(
        resolvePracsysApiPath() + path,
        body,
        {
            headers: {
                ...await getHeaders(),
                ...headers
            },
            params: {
                ...Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
            }
        }
    )

    //TODO: Error handling
    return response.data
}

const put = async (path: string, body: any, headers: any = {}) => {
    const response = await axios.put(
        resolvePracsysApiPath() + path,
        body,
        {
            headers: {
                ...await getHeaders(),
                ...headers
            }
        }
    )

    //TODO: Error handling
    return response.data
}

const httpDelete = async (path: string, headers: any = {}) => {
    const response = await axios.delete(
        resolvePracsysApiPath() + path,
        {
            headers: {
                ...await getHeaders(),
                ...headers
            }
        }
    )

    //TODO: Error handling
    return response.data
}

// TODO generics
const patch = async (path: string, body: any, headers: any = {}): Promise<Either<Problem, any>> => {
    return doCatch(async () => {
        const response = await axios.patch(
            resolvePracsysApiPath() + path,
            body,
            {
                headers: {
                    ...await getHeaders(),
                    ...headers
                }
            }
        )

        return response.data
    })
}

export type HttpResponse = {
    config: any
    data: any
    headers: any
    request: any
    status: number
    statusText: string
}

const isHttpResponse = (response: any): response is HttpResponse => {
    return 'config' in response
        && 'data' in response
        && 'headers' in response
        && 'request' in response
        && 'status' in response
        && 'statusText' in response
}

export {get, anonymousGet, post, put, httpDelete, patch, isHttpResponse}